// Zoom Video SDK rejects with plain objects ({ type, reason, errorCode }), which Sentry
// can only report as "Non-Error promise rejection" with no usable title or stack.
// This turns them into a real Error whose message names the operation and the reason.

interface SdkErrorShape {
   type?: unknown;
   reason?: unknown;
   errorCode?: unknown;
   message?: unknown;
}

const asText = (value: unknown): string =>
   typeof value === 'string' || typeof value === 'number' ? String(value) : '';

export const toSdkError = (error: unknown, operation: string): Error => {
   if (error instanceof Error) return error;
   const shape: SdkErrorShape = typeof error === 'object' && error !== null ? error : {};
   const type = asText(shape.type);
   const reason = asText(shape.reason) || asText(shape.message);
   const code = asText(shape.errorCode);
   const detail =
      [type, reason].filter(Boolean).join(': ') || (typeof error === 'string' ? error : 'unknown');
   const result = new Error(`Zoom SDK ${operation} failed: ${detail}`);
   result.name = 'ZoomSdkError';
   return Object.assign(result, { sdkType: type, sdkReason: reason, sdkErrorCode: code });
};

// SDK reasons are either free text (high cardinality, would split one failure into many
// issues) or a constant such as USER_MEDIA_DENIED. Only the constant form is a stable
// grouping key.
const ENUM_LIKE_REASON = /^[A-Z][A-Z0-9_]{2,63}$/;

export const sdkErrorFingerprint = (operation: string, error: Error): string[] => {
   const { sdkType, sdkReason } = error as { sdkType?: string; sdkReason?: string };
   const fingerprint = ['zoom-sdk', operation, sdkType || 'unknown'];
   if (sdkReason && ENUM_LIKE_REASON.test(sdkReason)) fingerprint.push(sdkReason);
   return fingerprint;
};
